/**
 * Roster store — the week's rows, keyed by plant and week.
 *
 * One fetch owns the five parallel reads the roster needs (orders, standards,
 * assignments, shifts, availability); a lighter one refreshes the assignments (and the
 * availability badges) after a manual move so the rest of the board does not flash; a third owns the
 * standing pins. Hooks in roster/controller dispatch these and select from
 * here instead of holding their own copies.
 */
import { createAsyncThunk,createSlice,PayloadAction } from "@reduxjs/toolkit"; // eslint-disable-line sort-imports
import { endOfWeek, parseISO } from "date-fns";

import { LaborStandard, laborStandardsApi } from "../../../../services/labor-standards-api";
import { productionPlanApi } from "../../../../services/production-plan-api";
import rosterAssignmentsApi, { AvailabilityRow } from "../../../../services/roster-assignments-api";
import shiftsApi, { ShiftInstance } from "../../../../services/shifts-api";
import { apiErrorMessage } from "../../../../utils/api-error";
import { toLocalIso, weekEndIso } from "../../utils/time";
import { RosterState, RosterWeek, RosterWeekKey } from "./roster-types";

export const rosterWeekKey = ({ locationId, weekStart }: RosterWeekKey): string => `${locationId}:${weekStart}`;

/** The ISO range the week's reads ask for: Monday 00:00 local to Sunday 23:59:59.999 local. */
const weekRange = (weekStart: string): { from: string; to: string } => {
	const monday = parseISO(weekStart);
	return { from: toLocalIso(monday), to: toLocalIso(endOfWeek(monday, { weekStartsOn: 1 })) };
};

const emptyWeek = (): RosterWeek => ({
	orders: [],
	standards: [],
	assignments: [],
	shifts: [],
	availability: [],
	pins: [],
	weekStatus: "idle",
	weekError: null,
	pinsStatus: "idle",
	pinsError: null,
});

const initialState: RosterState = { weeks: {} };

export const fetchRosterWeek = createAsyncThunk(
	"roster/fetchWeek",
	async (key: RosterWeekKey, { rejectWithValue }) => {
		const { from, to } = weekRange(key.weekStart);
		try {
			// Orders and assignments ARE the board; the rest are overlays. One failing
			// overlay call (a 401 while the token refreshes, a plant whose API lacks an
			// endpoint) must not blank the week, so overlays settle independently.
			// All at once: the overlays do not wait for the board's own reads. Who is
			// out comes on its own (fetchRosterAvailability) so the board does not wait for it.
			const overlays = Promise.allSettled([
				laborStandardsApi.getAllCached(),
				shiftsApi.getShiftsForDateRange(from, to),
			]);
			const [orders, assignments] = await Promise.all([
				productionPlanApi.getByDateRange(from, to),
				rosterAssignmentsApi.getByDateRange(from, to),
			]);
			const settled = await overlays;
			const overlay = <T,>(result: PromiseSettledResult<T>, what: string, fallback: T): T => {
				if (result.status === "fulfilled") {
					return result.value;
				}
				console.warn(`Failed to fetch roster ${what}:`, result.reason);
				return fallback;
			};
			const standards = overlay(settled[0], "labor standards", [] as LaborStandard[]);
			const shifts = overlay(settled[1], "shifts", [] as ShiftInstance[]);
			return { orders, standards, assignments, shifts };
		} catch (error) {
			console.error("Failed to fetch roster data:", error);
			return rejectWithValue(apiErrorMessage(error, "Failed to fetch roster data."));
		}
	}
);

/**
 * Who is out (time off, call-outs, people off their shift) for the week: the badges
 * on the board. Read alongside the week but not awaited by it — the slowest read,
 * and the board is usable without it; a failure leaves the badges as they were.
 */
export const fetchRosterAvailability = createAsyncThunk(
	"roster/fetchAvailability",
	async (key: RosterWeekKey, { rejectWithValue }) => {
		try {
			return await rosterAssignmentsApi.getAvailability(key.locationId, key.weekStart, weekEndIso(parseISO(key.weekStart)));
		} catch (error) {
			console.warn("Failed to fetch roster availability:", error);
			return rejectWithValue(apiErrorMessage(error, "Failed to fetch who is out."));
		}
	}
);

export const refetchRosterAssignments = createAsyncThunk(
	"roster/refetchAssignments",
	async (key: RosterWeekKey, { rejectWithValue }) => {
		const { from, to } = weekRange(key.weekStart);
		try {
			// Availability rides along: a move can put someone who is out onto a
			// line, or infer a worker as unavailable, so the badges must follow the
			// rows. It stays an overlay — a failed read keeps the badges on screen.
			const [assignments, availability] = await Promise.all([
				rosterAssignmentsApi.getByDateRange(from, to),
				rosterAssignmentsApi
					.getAvailability(key.locationId, key.weekStart, weekEndIso(parseISO(key.weekStart)))
					.catch((error: unknown) => {
						console.warn("Failed to refetch roster availability:", error);
						return null;
					}),
			]);
			return { assignments, availability };
		} catch (error) {
			console.error("Failed to refetch roster assignments:", error);
			return rejectWithValue(apiErrorMessage(error, "Failed to refetch roster assignments."));
		}
	}
);

export const fetchRosterPins = createAsyncThunk(
	"roster/fetchPins",
	async (key: RosterWeekKey, { rejectWithValue }) => {
		try {
			return await rosterAssignmentsApi.getPins(key.locationId, key.weekStart, weekEndIso(parseISO(key.weekStart)));
		} catch (error) {
			console.error("Failed to load standing crew pins:", error);
			return rejectWithValue("Could not load the standing crew.");
		}
	}
);

const weekOf = (state: RosterState, key: RosterWeekKey): RosterWeek => {
	const id = rosterWeekKey(key);
	const existing = state.weeks[id];
	if (existing) {
		return existing;
	}
	const created = emptyWeek();
	state.weeks[id] = created;
	return created;
};

const rosterSlice = createSlice({
	name: "roster",
	initialState,
	reducers: {
		/** Drop a week's rows (a sign-out, or a plant that no longer exists). */
		clearRosterWeek: (state, action: PayloadAction<RosterWeekKey>) => {
			delete state.weeks[rosterWeekKey(action.payload)];
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchRosterWeek.pending, (state, action) => {
				const week = weekOf(state, action.meta.arg);
				week.weekStatus = "pending";
				week.weekError = null;
			})
			.addCase(fetchRosterWeek.fulfilled, (state, action) => {
				const week = weekOf(state, action.meta.arg);
				week.orders = action.payload.orders;
				week.standards = action.payload.standards;
				week.assignments = action.payload.assignments;
				week.shifts = action.payload.shifts;
				week.weekStatus = "fulfilled";
			})
			.addCase(fetchRosterAvailability.fulfilled, (state, action) => {
				weekOf(state, action.meta.arg).availability = action.payload;
			})
			.addCase(fetchRosterWeek.rejected, (state, action) => {
				const week = weekOf(state, action.meta.arg);
				week.weekStatus = "error";
				week.weekError = (action.payload as string | undefined) ?? action.error.message ?? null;
			})
			.addCase(refetchRosterAssignments.fulfilled, (state, action) => {
				const week = weekOf(state, action.meta.arg);
				week.assignments = action.payload.assignments;
				if (action.payload.availability) {
					week.availability = action.payload.availability;
				}
			})
			.addCase(fetchRosterPins.pending, (state, action) => {
				const week = weekOf(state, action.meta.arg);
				week.pinsStatus = "pending";
				week.pinsError = null;
			})
			.addCase(fetchRosterPins.fulfilled, (state, action) => {
				const week = weekOf(state, action.meta.arg);
				week.pins = action.payload;
				week.pinsStatus = "fulfilled";
			})
			.addCase(fetchRosterPins.rejected, (state, action) => {
				const week = weekOf(state, action.meta.arg);
				week.pinsStatus = "error";
				week.pinsError = (action.payload as string | undefined) ?? action.error.message ?? null;
			});
	},
});

export const { clearRosterWeek } = rosterSlice.actions;

/** The stored week, or an empty one — callers never see `undefined`. */
const EMPTY_WEEK: RosterWeek = emptyWeek();
export const selectRosterWeek = (state: { roster: RosterState }, key: RosterWeekKey): RosterWeek =>
	state.roster.weeks[rosterWeekKey(key)] ?? EMPTY_WEEK;

export default rosterSlice.reducer;
